import { z } from 'zod'
import { Model } from './architecture'
import { Market, Markets } from '../shared/domain/Market'
import { DomainError } from './DomainError'
import { ErrorsCode } from './ErrorsCode'

export type Currencies = (typeof ALLOWED_CURRENCY)[number]
export const ALLOWED_CURRENCY = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'DKK',
  'NOK',
  'PLN',
  'RON',
  'CHF',
  'ZAR',
  'SEK',
  'TRY',
  'AUD',
  'MXN',
  'BRL',
  'NZD',
  'HUF',
  'CZK',
  'SAR',
  'AED',
] as const

const FROM_MARKET_TO_CURRENCY: Record<Markets, Currencies> = {
  ae: 'AED',
  at: 'EUR',
  au: 'AUD',
  be: 'EUR',
  br: 'BRL',
  ca: 'CAD',
  ch: 'CHF',
  cz: 'CZK',
  de: 'EUR',
  dk: 'DKK',
  ee: 'EUR',
  eg: 'USD',
  es: 'EUR',
  fi: 'EUR',
  fr: 'EUR',
  gr: 'EUR',
  hr: 'EUR',
  hu: 'HUF',
  ie: 'EUR',
  it: 'EUR',
  lt: 'EUR',
  lv: 'EUR',
  mx: 'MXN',
  nl: 'EUR',
  no: 'NOK',
  nz: 'NZD',
  pl: 'PLN',
  pt: 'EUR',
  ro: 'RON',
  sa: 'SAR',
  se: 'SEK',
  si: 'EUR',
  sk: 'EUR',
  tr: 'TRY',
  uk: 'GBP',
  us: 'USD',
  za: 'ZAR',
  co: 'USD',
}

export const CurrencyValidation = z.object({
  value: z.enum(ALLOWED_CURRENCY, { required_error: '[Currency] value is required' }),
})

export class Currency extends Model {
  static create({ value }: z.infer<typeof CurrencyValidation>) {
    CurrencyValidation.parse({ value })
    return new Currency(value)
  }

  static createOrUndefined({ value }: { value: Currencies | undefined }) {
    if (!value) return undefined

    return Currency.create({ value })
  }

  static fromMarket({ market }: { market: Market }) {
    if (!market.value) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    return Currency.create({ value: FROM_MARKET_TO_CURRENCY[market.value] })
  }

  static empty() {
    return new Currency('USD')
  }

  constructor(private readonly _value: Currencies) {
    super()
  }

  get value() {
    return this._value
  }

  toJSON() {
    return { value: this.value }
  }
}
